/* SE2 · Motion (2026-09-18). What each part draws is in assets/se2-motion.css. Progressive on purpose: gated
   on prefers-reduced-motion and on this script actually running, and every hook is additive — nothing here
   is required for the page to work. */
(() => {
  'use strict';

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const html = document.documentElement;

  /* ---- 1. reveal on scroll ----
     Repeating elements that earn a rise. Each is indexed among its siblings so a list staggers within its
     parent. The kit builder keeps its own motion, and cart-drawer rows keep Horizon's enter/leave animation,
     so both are left alone. A child of an already-marked element is never marked twice. */
  const REVEAL = [
    '.se2-hdiff__card',
    '.se2-htab',
    '.se2-faq__item',
    '.se2-vb__content > *',
    '.product-grid__item',
    '.se2-card',
    '.se2-fn__note',
    '.se2-fn__card',
    '.se2-fn__meter',
    // '.se2-dpdp3__trust-item', '.se2-dpdp3__chip' and '.se2-fpdp3__chip' went with the 2026-09-25 PDP rebuild; the
    // new diffuser tiles and benefits render without a reveal.
    '.se2-machine-compare__row',
  ].join(',');
  const EXCLUDE = '#cart-drawer, cart-drawer-component, .cart-drawer, .se2-kit';

  let io;

  const indexAmongSiblings = (el) => {
    const parent = el.parentElement;
    if (!parent) return 0;
    let i = 0;
    for (const sib of parent.children) {
      if (sib === el) break;
      if (sib.hasAttribute('data-se2-reveal') && !sib.hidden) i += 1;
    }
    return Math.min(i, 8);
  };

  // A horizontal rail (a scroller wider than itself) reveals as one: its cards rise together when the rail
  // comes on screen, instead of a card fading in halfway through a swipe because it was off to the right.
  const rails = new WeakSet();
  const isRail = (node) => {
    const style = getComputedStyle(node);
    return /(auto|scroll)/.test(style.overflowX) && node.scrollWidth > node.clientWidth + 8;
  };
  // The scroller is usually the parent, but a card can sit in an <li> or a wrapper inside it: look three up.
  const findRail = (el) => {
    let node = el.parentElement;
    for (let depth = 0; node && depth < 3; depth += 1) {
      if (isRail(node)) return node;
      node = node.parentElement;
    }
    return null;
  };

  const arm = (el) => {
    if (el.hasAttribute('data-se2-reveal')) return;
    if (el.closest(EXCLUDE)) return;
    if (el.parentElement && el.parentElement.closest('[data-se2-reveal]')) return;
    el.setAttribute('data-se2-reveal', '');
    el.style.setProperty('--se2-reveal-i', String(indexAmongSiblings(el)));
    if (el.matches('.se2-fn__meter')) {
      el.querySelectorAll('.se2-fn__pip').forEach((pip, i) => pip.style.setProperty('--se2-pip-i', String(i)));
    }
    const rail = findRail(el);
    if (rail) {
      rail.setAttribute('data-se2-rail', '');
      if (!rails.has(rail)) {
        rails.add(rail);
        io.observe(rail);
      }
      return;
    }
    io.observe(el);
  };

  const reveal = (el) => {
    el.classList.add('is-revealed');
    io.unobserve(el);
  };

  const armAll = (root) => {
    (root || document).querySelectorAll(REVEAL).forEach(arm);
  };

  /* ---- 5. the cart total ticks when it changes (shares the mutation observer below) ---- */
  // Seeded from the page as it loaded: an empty cart has no total element, so the first total that appears
  // after an add is itself a change worth a tick, while a total already on the page waits until it moves.
  const readTotal = () => {
    const el = document.querySelector('.cart-totals__total-value');
    return el ? el.textContent.trim() : '';
  };
  let lastTotal = readTotal();
  let totalFrame = 0;
  const checkTotal = () => {
    totalFrame = 0;
    const el = document.querySelector('.cart-totals__total-value');
    const now = el ? el.textContent.trim() : '';
    if (el && now !== lastTotal) {
      el.classList.remove('se2-tick');
      void el.offsetWidth;
      el.classList.add('se2-tick');
    }
    lastTotal = now;
  };

  try {
    io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const target = entry.target;
          if (target.hasAttribute('data-se2-rail')) {
            target.querySelectorAll('[data-se2-reveal]').forEach((card) => card.classList.add('is-revealed'));
            io.unobserve(target);
            continue;
          }
          reveal(target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    html.classList.add('se2-motion');
    armAll();

    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches(REVEAL)) arm(node);
          armAll(node);
        }
      }
      if (!totalFrame) totalFrame = requestAnimationFrame(checkTotal);
    }).observe(document.body, { childList: true, subtree: true, characterData: true });

    document.addEventListener('shopify:section:load', () => armAll());
  } catch (error) {
    // Without the observer nothing may stay hidden: drop the gate and let the plain page through.
    html.classList.remove('se2-motion');
    return;
  }

  /* ---- 1b. a filtered grid rises again in its new order ---- */
  const restagger = (selector) => {
    const items = Array.from(document.querySelectorAll(selector)).filter(
      (el) => !el.hidden && el.hasAttribute('data-se2-reveal')
    );
    if (items.length === 0) return;
    items.forEach((el, i) => {
      el.classList.remove('is-revealed');
      el.style.setProperty('--se2-reveal-i', String(Math.min(i, 8)));
    });
    void document.body.offsetHeight;
    requestAnimationFrame(() => items.forEach((el) => el.classList.add('is-revealed')));
  };

  document.addEventListener('click', (event) => {
    const chip = event.target.closest('[data-se2-filter], [data-se2-filter-clear], [data-se2-space-filter]');
    if (!chip) return;
    setTimeout(() => restagger('.product-grid__item'), 30);
  });

  /* ---- 3. disclosures open and close with their height instead of snapping ---- */
  const PANELS = {
    'details.se2-faq__item': '.se2-faq__a',
    'details.se2-po__benefits': '.se2-po__bullets',
  };
  const EASE = 'cubic-bezier(0.2, 0.7, 0.2, 1)';

  document.addEventListener('click', (event) => {
    const summary = event.target.closest('summary');
    if (!summary) return;
    const details = summary.parentElement;
    if (!details || details.tagName !== 'DETAILS') return;
    const key = Object.keys(PANELS).find((selector) => details.matches(selector));
    if (!key) return;
    const panel = details.querySelector(PANELS[key]);
    if (!panel || details.dataset.se2Animating) return;

    event.preventDefault();
    details.dataset.se2Animating = '1';
    panel.style.overflow = 'hidden';
    const done = () => {
      panel.style.height = '';
      panel.style.overflow = '';
      delete details.dataset.se2Animating;
    };

    if (details.open) {
      const from = panel.getBoundingClientRect().height;
      const closing = panel.animate(
        [
          { height: `${from}px`, opacity: 1 },
          { height: '0px', opacity: 0 },
        ],
        { duration: 260, easing: EASE }
      );
      closing.onfinish = () => {
        details.open = false;
        done();
      };
      closing.oncancel = done;
    } else {
      details.open = true;
      const to = panel.getBoundingClientRect().height;
      const opening = panel.animate(
        [
          { height: '0px', opacity: 0 },
          { height: `${to}px`, opacity: 1 },
        ],
        { duration: 320, easing: EASE }
      );
      opening.onfinish = done;
      opening.oncancel = done;
    }
  });

  /* ---- 6. one-time nudge on a phone rail: the row slides 36px and back once it is on screen, once per
     session, and never if the visitor has already touched or wheeled it. The reveal above gets a head start
     so the two motions do not overlap. ---- */
  const nudge = () => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    for (const row of document.querySelectorAll('[data-se2-nudge-row]')) {
      if (row.dataset.se2Nudged || row.scrollWidth <= row.clientWidth + 8) continue;
      // A rail marked data-se2-nudge-always (the buy box's benefit chips) hints on every visit, as the gallery
      // does since 2026-09-22; the rest keep the once-a-session claim.
      if (!row.hasAttribute('data-se2-nudge-always')) {
        const key = `se2-nudge:${row.dataset.se2NudgeRow || row.id || location.pathname}`;
        try {
          if (sessionStorage.getItem(key)) continue;
          sessionStorage.setItem(key, '1');
        } catch (error) {
          // Storage can be unavailable; a nudge every visit is the harmless fallback.
        }
      }
      row.dataset.se2Nudged = '1';
      const cancel = () => row.classList.remove('is-nudging');
      row.addEventListener('pointerdown', cancel, { once: true });
      row.addEventListener('wheel', cancel, { once: true, passive: true });
      const watch = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        watch.disconnect();
        setTimeout(() => {
          row.classList.add('is-nudging');
          setTimeout(cancel, 1600);
        }, 900);
      });
      watch.observe(row);
    }
  };

  /* ---- 7. a scroller shows its next image once, so the swipe is discoverable ----
     A transform nudge would fight scroll-snap, so this drives scrollLeft directly with snapping suspended for
     the duration and restored afterwards. Out, hold, back: the hold is what lets the eye land on the second
     image rather than catching a flicker. Only on a phone, only while the scroller is still at the start, once
     a session, and abandoned the moment the shopper touches it. */
  const easeOut = (x) => 1 - Math.pow(1 - x, 3);
  const easeInOut = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);

  const bumpScroller = (scroller, { ratio = 0.24, max = 96, delay = 1100 } = {}) => {
    const watch = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      watch.disconnect();
      setTimeout(() => {
        if (scroller.scrollLeft > 4) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const snap = scroller.style.scrollSnapType;
        scroller.style.scrollSnapType = 'none';
        const peak = Math.min(max, scroller.clientWidth * ratio);
        const OUT = 420;
        const HOLD = 280;
        const BACK = 520;
        const total = OUT + HOLD + BACK;
        let start = null;
        let cancelled = false;

        const stop = () => {
          cancelled = true;
          scroller.style.scrollSnapType = snap;
        };
        scroller.addEventListener('pointerdown', stop, { once: true });
        scroller.addEventListener('wheel', stop, { once: true, passive: true });

        const step = (now) => {
          if (cancelled) return;
          if (start === null) start = now;
          const elapsed = Math.min(now - start, total);
          let offset;
          if (elapsed < OUT) offset = easeOut(elapsed / OUT) * peak;
          else if (elapsed < OUT + HOLD) offset = peak;
          else offset = peak * (1 - easeInOut((elapsed - OUT - HOLD) / BACK));
          scroller.scrollLeft = offset;
          if (elapsed < total) {
            requestAnimationFrame(step);
            return;
          }
          scroller.scrollLeft = 0;
          scroller.style.scrollSnapType = snap;
          scroller.removeEventListener('pointerdown', stop);
        };
        requestAnimationFrame(step);
      }, delay);
    });
    watch.observe(scroller);
  };

  const claimOnce = (key) => {
    try {
      if (sessionStorage.getItem(key)) return false;
      sessionStorage.setItem(key, '1');
    } catch (error) {
      // Private browsing: a hint per visit is the harmless fallback.
    }
    return true;
  };

  const galleryBump = () => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    for (const scroller of document.querySelectorAll('media-gallery slideshow-slides')) {
      if (scroller.dataset.se2Bumped) continue;
      if (scroller.scrollWidth <= scroller.clientWidth + 8) continue;
      // Every page load, not once a session (owner's call 2026-09-22): the session claim meant anyone who
      // had already opened a product that day never saw the hint again, and it reads as absent. It still
      // only runs while the gallery is at its start, and is abandoned the moment the shopper touches it.
      scroller.dataset.se2Bumped = '1';
      bumpScroller(scroller);
    }
  };

  /* ---- 7b. one product card demonstrates that its images swipe ----
     Only the FIRST card with more than one image, never the whole grid: twenty cards moving at once reads as a
     fault, while one is a demonstration. The card is narrow, so the travel is a larger share of its width to
     show a comparable slice of the second image. */
  const cardBump = () => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    const card = Array.from(document.querySelectorAll('.card-gallery slideshow-slides')).find(
      (scroller) => scroller.scrollWidth > scroller.clientWidth + 8 && !scroller.dataset.se2Bumped
    );
    if (!card) return;
    if (!claimOnce(`se2-card-bump:${location.pathname}`)) return;
    card.dataset.se2Bumped = '1';
    bumpScroller(card, { ratio: 0.3, max: 72, delay: 1300 });
  };

  /* ---- 8. the floating header steps back while the gallery is swiped ----
     On a phone the capsule sits over the product gallery, so it covers the top of the very image the shopper
     is moving to. While the gallery is scrolling, html gets data-se2-media-dim and the capsule drops to a bare
     ink wash (assets/se2-header-pill.css); the flag clears 1.4s after the last scroll, which is long enough to
     ride out the gap between two swipes instead of flickering. */
  const DIM_DELAY = 1400;
  let dimTimer = 0;

  const watchGalleryScroll = () => {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    for (const scroller of document.querySelectorAll('media-gallery slideshow-slides')) {
      if (scroller.dataset.se2DimBound) continue;
      scroller.dataset.se2DimBound = '1';
      scroller.addEventListener(
        'scroll',
        () => {
          // Only while the capsule actually sits over the gallery. On the fragrance PDP the title, rating and
          // price now come first (2026-09-24), so at the top of the page the gallery is well below the header
          // and the load-time nudge was dimming the capsule over plain text.
          const header = document.getElementById('header-component');
          const headerBottom = header ? header.getBoundingClientRect().bottom : 0;
          if (scroller.getBoundingClientRect().top > headerBottom) return;
          html.setAttribute('data-se2-media-dim', '');
          window.clearTimeout(dimTimer);
          dimTimer = window.setTimeout(() => html.removeAttribute('data-se2-media-dim'), DIM_DELAY);
        },
        { passive: true }
      );
    }
  };

  nudge();
  galleryBump();
  cardBump();
  watchGalleryScroll();
  // A rail filled by another script (the buy box's benefit chips, from the purchase script) asks for its nudge
  // by dispatching this once its content exists. Script order is not guaranteed — on a warm cache the purchase
  // module has run before this file, so the chips are already there for the pass above, and on a cold load they
  // arrive later and this listener catches them. Either way the row is nudged exactly once.
  document.addEventListener('se2:rail', () => nudge());
  document.addEventListener('shopify:section:load', () => {
    nudge();
    galleryBump();
    cardBump();
    watchGalleryScroll();
  });
})();
